import React from 'react';
import { useAppState } from '../App';
import { useEffectOnce } from './useEffectOnce';

const genererCacheNokkel = (args: string[], id?: string) =>
    `${id ? id + '-' : ''}${args.join('-')}`;
const cache: { [key: string]: unknown } = {};

const harTekniskFeil = (errorMessage: string) =>
    errorMessage.startsWith('40') || errorMessage.startsWith('500') || false;

export type UseApiProps<T> = {
    hent: (...args: any[]) => Promise<void>;
    laster: boolean;
    data?: T;
    feilmelding?: string;
    timestamp?: number;
};

export function useApi<T>(
    fetcher: (...args: any[]) => Promise<T>,
    options?: { id?: string; caching?: boolean },
): UseApiProps<T> {
    const caching = options?.caching ?? true;
    const [data, setData] = React.useState<T>();
    const [feilmelding, setFeilmelding] = React.useState<string>();
    const [laster, setLaster] = React.useState(false);
    const [timestamp, setTimestamp] = React.useState<number>();
    const setTekniskFeil = useAppState((state) => state.setTekniskFeil);

    useEffectOnce(() => {
        setTekniskFeil(undefined);
    });

    const hent = React.useCallback(
        (...args: string[]) => {
            const cacheNokkel = genererCacheNokkel(args, options?.id);
            const cachetResultat = cache[cacheNokkel] as T;

            if (cachetResultat && caching) {
                setData(cachetResultat);
                setTimestamp(Date.now());
                return Promise.resolve();
            }

            setData(undefined);
            setFeilmelding(undefined);
            setTekniskFeil(undefined);
            setLaster(true);

            return fetcher(...args)
                .then((res) => {
                    cache[cacheNokkel] = res;
                    setData(res);
                    setTimestamp(Date.now());
                })
                .catch((e) => {
                    delete cache[cacheNokkel];
                    setFeilmelding(e.message);

                    if (harTekniskFeil(e.message)) {
                        setTekniskFeil(e.message);
                    }
                })
                .finally(() => setLaster(false));
        },
        [fetcher, caching, options?.id, setTekniskFeil],
    );

    return {
        hent,
        feilmelding,
        data,
        laster,
        timestamp,
    };
}
